import React, { useState, useEffect } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const GBPAutomation = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [dashboard, setDashboard] = useState(null);
  const [posts, setPosts] = useState([]);
  const [responses, setResponses] = useState([]);
  const [qaSuggestions, setQaSuggestions] = useState([]);
  const [calendar, setCalendar] = useState([]);
  const [competitorUpdates, setCompetitorUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [postingToGBP, setPostingToGBP] = useState(null);
  const [gbpConnected, setGbpConnected] = useState(false);
  const [gbpAuthStatus, setGbpAuthStatus] = useState(null);
  
  // Review form state
  const [reviewForm, setReviewForm] = useState({
    reviewer_name: '',
    rating: 5,
    review_text: ''
  });

  // Spanish (Atención en Español) GBP signaling state
  const [esTemplates, setEsTemplates] = useState(null);
  const [esPosting, setEsPosting] = useState(null); // template index currently posting
  const [esBadgePosting, setEsBadgePosting] = useState(false);

  useEffect(() => {
    fetchData();
    checkGBPConnection();
    fetchEsTemplates();
  }, []);

  const fetchEsTemplates = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/gbp-posts/templates?language=es`);
      setEsTemplates(res.data.templates);
    } catch (e) {
      console.error('Failed to load Spanish templates:', e);
    }
  };

  const handlePostEsTemplate = async (tpl, key) => {
    if (!gbpConnected) {
      alert('GBP not connected. Go to Review Responses → Connect Google Business first.');
      return;
    }
    setEsPosting(key);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/gbp-posts/create`, {
        summary: tpl.summary,
        cta_type: tpl.cta_type,
        cta_url: tpl.cta_url,
        topic_type: 'STANDARD',
        language: 'es',
      });
      if (res.data.success) {
        alert(`✅ Publicado en GBP (es-US): ${tpl.title}`);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || error.message;
      alert('❌ Error al publicar: ' + detail);
    } finally {
      setEsPosting(null);
    }
  };

  const handlePostSpanishBadge = async () => {
    if (!gbpConnected) {
      alert('GBP not connected. Go to Review Responses → Connect Google Business first.');
      return;
    }
    if (!window.confirm('Publish the "Atención en Español" brand badge to GBP now? This signals Spanish-language service to Google Maps users.')) return;
    setEsBadgePosting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/gbp-posts/spanish-badge`);
      if (res.data.success) {
        alert(`✅ Atención en Español badge published (es-US)`);
      }
    } catch (error) {
      const detail = error.response?.data?.detail || error.message;
      alert('❌ Error: ' + detail);
    } finally {
      setEsBadgePosting(false);
    }
  };

  const checkGBPConnection = async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/google-business/auth/status`);
      setGbpConnected(res.data.authenticated);
      setGbpAuthStatus(res.data);
    } catch (error) {
      setGbpConnected(false);
      setGbpAuthStatus({ authenticated: false });
    }
  };

  const connectGoogleBusiness = () => {
    window.location.href = `${BACKEND_URL}/api/google-business/auth/start`;
  };

  const disconnectGoogleBusiness = async () => {
    if (!window.confirm('Disconnect Google Business Profile? You will need to reconnect to post to Google.')) return;
    try {
      await axios.post(`${BACKEND_URL}/api/google-business/auth/disconnect`);
      alert('✅ Google Business Profile disconnected');
      setGbpConnected(false);
      setGbpAuthStatus({ authenticated: false });
    } catch (err) {
      alert('❌ Failed to disconnect. Please try again.');
    }
  };

  const handlePostToGBP = async (post) => {
    setPostingToGBP(post.id);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/gbp-posts/create`, {
        summary: post.content.substring(0, 1500),
        cta_type: 'LEARN_MORE',
        cta_url: post.button_url || 'https://fortworthdental.com',
        topic_type: 'STANDARD'
      });
      
      if (response.data.success) {
        alert('Post published to Google Business Profile!');
        await handleUpdateStatus('posts', post.id, 'posted');
      }
    } catch (error) {
      console.error('Error posting to GBP:', error);
      const detail = error.response?.data?.detail || error.message;
      if (detail.includes('401') || detail.includes('not authenticated')) {
        alert('GBP not authenticated. Go to Review Responses page and click "Connect Google Business" to re-authenticate.');
      } else {
        alert('Failed to post to GBP: ' + detail);
      }
    } finally {
      setPostingToGBP(null);
    }
  };

  const [generatingImage, setGeneratingImage] = useState(null);

  const handleGenerateImage = async (postId) => {
    setGeneratingImage(postId);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/gbp/posts/${postId}/generate-image`);
      
      if (response.data.success) {
        // Update the local posts state with the new image
        setPosts(prev => prev.map(p => 
          p.id === postId 
            ? { ...p, image_base64: response.data.image_base64 }
            : p
        ));
        alert('✅ Image generated successfully!');
      }
    } catch (error) {
      console.error('Error generating image:', error);
      alert('❌ Failed to generate image: ' + (error.response?.data?.detail || error.message));
    } finally {
      setGeneratingImage(null);
    }
  };

  const downloadImage = (base64Data, filename) => {
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${base64Data}`;
    link.download = filename || 'gbp-post-image.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashRes, postsRes, responsesRes, qaRes, calRes, compRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/gbp/dashboard`),
        axios.get(`${BACKEND_URL}/api/gbp/posts`),
        axios.get(`${BACKEND_URL}/api/gbp/review-responses`),
        axios.get(`${BACKEND_URL}/api/gbp/qa-suggestions`),
        axios.get(`${BACKEND_URL}/api/gbp/calendar`),
        axios.get(`${BACKEND_URL}/api/gbp/competitor-updates`)
      ]);
      
      setDashboard(dashRes.data);
      setPosts(postsRes.data.posts || []);
      setResponses(responsesRes.data.responses || []);
      setQaSuggestions(qaRes.data.suggestions || []);
      setCalendar(calRes.data.items || []);
      setCompetitorUpdates(compRes.data.updates || []);
    } catch (error) {
      console.error('Failed to fetch GBP data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateAll = async () => {
    setGenerating(true);
    try {
      await axios.post(`${BACKEND_URL}/api/gbp/auto-generate-all`);
      alert('Content generation started! Refresh in a few moments to see new content.');
      setTimeout(fetchData, 5000);
    } catch (error) {
      console.error('Failed to generate content:', error);
      alert('Failed to start generation');
    } finally {
      setGenerating(false);
    }
  };

  const handleGeneratePosts = async () => {
    setGenerating(true);
    try {
      await axios.post(`${BACKEND_URL}/api/gbp/generate-posts?count=2`);
      alert('Generating 2 Google Posts...');
      setTimeout(fetchData, 5000);
    } catch (error) {
      alert('Failed to generate posts');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateQA = async () => {
    setGenerating(true);
    try {
      await axios.post(`${BACKEND_URL}/api/gbp/generate-qa?count=3`);
      alert('Generating 3 Q&A suggestions...');
      setTimeout(fetchData, 5000);
    } catch (error) {
      alert('Failed to generate Q&As');
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateReviewResponse = async (e) => {
    e.preventDefault();
    setGenerating(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/gbp/generate-review-response`, null, {
        params: reviewForm
      });
      setResponses([response.data, ...responses]);
      setReviewForm({ reviewer_name: '', rating: 5, review_text: '' });
      alert('Review response generated!');
    } catch (error) {
      alert('Failed to generate response');
    } finally {
      setGenerating(false);
    }
  };

  const handleCheckCompetitor = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/gbp/check-competitor`);
      alert('Competitor check started!');
      setTimeout(fetchData, 3000);
    } catch (error) {
      alert('Failed to check competitor');
    }
  };

  const handleGenerateCalendar = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/gbp/calendar/generate?weeks=4`);
      alert('Generated 4-week content calendar!');
      fetchData();
    } catch (error) {
      alert('Failed to generate calendar');
    }
  };

  const handleUpdateStatus = async (type, id, status) => {
    try {
      await axios.put(`${BACKEND_URL}/api/gbp/${type}/${id}`, null, {
        params: { status }
      });
      fetchData();
    } catch (error) {
      alert('Failed to update status');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">GBP Automation Center</h1>
          <p className="text-gray-600">Automate your Google Business Profile optimization</p>
        </div>
        <button
          onClick={handleGenerateAll}
          disabled={generating}
          className="bg-gradient-to-r from-teal-600 to-blue-600 text-white px-6 py-3 rounded-lg hover:from-teal-700 hover:to-blue-700 disabled:opacity-50 flex items-center gap-2 font-semibold shadow-lg"
        >
          {generating ? (
            <>
              <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
              </svg>
              Generating...
            </>
          ) : (
            <>🚀 Auto-Generate All Content</>
          )}
        </button>
      </div>

      {/* Google Business Profile connection status */}
      <div
        data-testid="gbp-connection-banner"
        className={`mb-6 p-4 rounded-lg border flex items-center justify-between ${
          gbpConnected ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'
        }`}
      >
        <div>
          <h3 className={`font-semibold ${gbpConnected ? 'text-green-800' : 'text-orange-800'}`}>
            {gbpConnected
              ? '✅ Google Business Profile Connected'
              : (gbpAuthStatus?.needs_reconnect
                  ? '⚠️ Google Connection Expired — Reconnect Needed'
                  : '⚠️ Google Business Profile Not Connected')}
          </h3>
          <p className={`text-sm ${gbpConnected ? 'text-green-700' : 'text-orange-700'}`}>
            {gbpConnected
              ? 'You can post content directly to Google Business Profile'
              : (gbpAuthStatus?.needs_reconnect
                  ? 'Your Google session expired (this happens periodically). Click Disconnect, then Connect to restore posting.'
                  : 'Connect your Google Business account to post to Google')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {gbpConnected ? (
            <button
              onClick={disconnectGoogleBusiness}
              data-testid="gbp-disconnect-btn"
              className="px-4 py-2 rounded-lg bg-white border border-red-200 text-red-600 hover:bg-red-50 text-sm font-medium"
            >
              Disconnect
            </button>
          ) : (
            <button
              onClick={connectGoogleBusiness}
              data-testid="gbp-connect-btn"
              className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm font-semibold"
            >
              Connect Google Business
            </button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      {dashboard && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-blue-500">
            <p className="text-gray-600 text-sm">Google Posts</p>
            <p className="text-2xl font-bold text-gray-800">{dashboard.posts?.total || 0}</p>
            <p className="text-xs text-gray-500">{dashboard.posts?.drafts || 0} drafts ready</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-green-500">
            <p className="text-gray-600 text-sm">Review Responses</p>
            <p className="text-2xl font-bold text-gray-800">{dashboard.review_responses?.total || 0}</p>
            <p className="text-xs text-gray-500">{dashboard.review_responses?.pending || 0} pending</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-purple-500">
            <p className="text-gray-600 text-sm">Q&A Suggestions</p>
            <p className="text-2xl font-bold text-gray-800">{dashboard.qa_suggestions?.total || 0}</p>
            <p className="text-xs text-gray-500">{dashboard.qa_suggestions?.drafts || 0} to add</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-orange-500">
            <p className="text-gray-600 text-sm">Competitor Alerts</p>
            <p className="text-2xl font-bold text-gray-800">{dashboard.competitor?.new_updates || 0}</p>
            <p className="text-xs text-gray-500">new updates</p>
          </div>
          <div className="bg-white p-4 rounded-lg shadow border-l-4 border-teal-500">
            <p className="text-gray-600 text-sm">Calendar Items</p>
            <p className="text-2xl font-bold text-gray-800">{dashboard.calendar?.upcoming || 0}</p>
            <p className="text-xs text-gray-500">upcoming</p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6 overflow-x-auto">
        {[
          { id: 'dashboard', label: '📊 Dashboard', count: null },
          { id: 'posts', label: '📝 Google Posts', count: posts.length },
          { id: 'reviews', label: '⭐ Review Responses', count: responses.length },
          { id: 'qa', label: '❓ Q&A', count: qaSuggestions.length },
          { id: 'competitor', label: '🔍 Competitor', count: competitorUpdates.length },
          { id: 'calendar', label: '📅 Calendar', count: calendar.length },
          { id: 'spanish', label: '🇲🇽 Atención en Español', count: esTemplates ? (esTemplates.brand?.length || 0) + (esTemplates.offers?.length || 0) + (esTemplates.services?.length || 0) : null },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 font-medium whitespace-nowrap ${
              activeTab === tab.id 
                ? 'text-teal-600 border-b-2 border-teal-600' 
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab.label} {tab.count !== null && `(${tab.count})`}
          </button>
        ))}
      </div>

      {/* Dashboard Tab */}
      {activeTab === 'dashboard' && dashboard && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="font-semibold text-gray-800 mb-4">⚡ Quick Actions</h2>
            <div className="grid grid-cols-2 gap-3">
              <button 
                onClick={handleGeneratePosts}
                disabled={generating}
                className="bg-blue-50 text-blue-700 p-3 rounded-lg hover:bg-blue-100 text-sm font-medium"
              >
                📝 Generate Posts
              </button>
              <button 
                onClick={handleGenerateQA}
                disabled={generating}
                className="bg-purple-50 text-purple-700 p-3 rounded-lg hover:bg-purple-100 text-sm font-medium"
              >
                ❓ Generate Q&As
              </button>
              <button 
                onClick={handleCheckCompetitor}
                className="bg-orange-50 text-orange-700 p-3 rounded-lg hover:bg-orange-100 text-sm font-medium"
              >
                🔍 Check Competitor
              </button>
              <button 
                onClick={handleGenerateCalendar}
                className="bg-teal-50 text-teal-700 p-3 rounded-lg hover:bg-teal-100 text-sm font-medium"
              >
                📅 Generate Calendar
              </button>
            </div>
          </div>

          {/* Business Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="font-semibold text-gray-800 mb-4">🏢 Your Business</h2>
            <div className="space-y-2 text-sm">
              <p><strong>Name:</strong> {dashboard.business_info?.name}</p>
              <p><strong>Reviews:</strong> {dashboard.business_info?.reviews_count} ⭐ {dashboard.business_info?.rating}</p>
              <p><strong>Award:</strong> 🏆 {dashboard.business_info?.award}</p>
              <p><strong>Phone:</strong> {dashboard.business_info?.phone}</p>
              <a 
                href={dashboard.business_info?.review_link} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-teal-600 hover:underline block mt-2"
              >
                View Google Reviews →
              </a>
            </div>
          </div>

          {/* Competitor Info */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="font-semibold text-gray-800 mb-4">🎯 Competitor Tracking</h2>
            <div className="space-y-2 text-sm">
              <p><strong>Name:</strong> {dashboard.business_info?.competitor?.name}</p>
              <p><strong>Domain:</strong> {dashboard.business_info?.competitor?.domain}</p>
              <p><strong>Reviews:</strong> {dashboard.business_info?.competitor?.reviews} ⭐ {dashboard.business_info?.competitor?.rating}</p>
              <div className="mt-3 p-3 bg-green-50 rounded-lg">
                <p className="text-green-700 font-medium">Your Advantage:</p>
                <p className="text-green-600 text-xs">You have {parseInt(dashboard.business_info?.reviews_count?.replace(/[^0-9]/g, '') || 0) - parseInt(dashboard.business_info?.competitor?.reviews || 0)} more reviews!</p>
              </div>
            </div>
          </div>

          {/* Your Differentiators */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="font-semibold text-gray-800 mb-4">💪 Your Differentiators</h2>
            <ul className="space-y-2 text-sm">
              {dashboard.business_info?.differentiators?.map((diff, i) => (
                <li key={i} className="flex items-start gap-2">
                  <span className="text-green-500">✓</span>
                  <span>{diff}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Posts Tab */}
      {activeTab === 'posts' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center flex-wrap gap-2">
            <div>
              <h2 className="font-semibold text-gray-800">Generated Google Posts</h2>
              <p className={`text-sm ${gbpConnected ? 'text-green-600' : 'text-orange-600'}`}>
                {gbpConnected ? '✓ Connected to Google Business Profile' : '⚠️ Not connected to GBP - Go to Review Responses to connect'}
              </p>
            </div>
            <button 
              onClick={handleGeneratePosts}
              disabled={generating}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              + Generate New Posts
            </button>
          </div>
          
          {posts.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              <p>No posts generated yet. Click "Generate New Posts" to create content!</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {posts.map(post => (
                <div key={post.id} className="bg-white rounded-lg shadow p-4">
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-semibold text-gray-800">{post.title}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      post.status === 'posted' ? 'bg-green-100 text-green-800' :
                      post.status === 'draft' ? 'bg-gray-100 text-gray-800' :
                      'bg-yellow-100 text-yellow-800'
                    }`}>
                      {post.status}
                    </span>
                  </div>
                  
                  {/* Image Section */}
                  {post.image_base64 ? (
                    <div className="mb-3">
                      <img 
                        src={`data:image/png;base64,${post.image_base64}`}
                        alt={post.title}
                        className="w-full max-h-64 object-cover rounded-lg"
                      />
                      <button
                        onClick={() => downloadImage(post.image_base64, `${post.title.replace(/\s+/g, '-')}.png`)}
                        className="mt-2 text-sm text-teal-600 hover:text-teal-800"
                      >
                        📥 Download Image
                      </button>
                    </div>
                  ) : (
                    <div className="mb-3 p-4 bg-gray-100 rounded-lg text-center">
                      <p className="text-gray-500 text-sm mb-2">No image generated yet</p>
                      <button
                        onClick={() => handleGenerateImage(post.id)}
                        disabled={generatingImage === post.id}
                        className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50 text-sm"
                      >
                        {generatingImage === post.id ? '⏳ Generating (~30s)...' : '🎨 Generate AI Image'}
                      </button>
                    </div>
                  )}
                  
                  <div className="bg-gray-50 p-3 rounded-lg mb-3 text-sm whitespace-pre-wrap max-h-48 overflow-y-auto">
                    {post.content}
                  </div>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {post.hashtags?.map((tag, i) => (
                      <span key={i} className="bg-blue-100 text-blue-700 px-2 py-1 rounded text-xs">{tag}</span>
                    ))}
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    <button 
                      onClick={() => copyToClipboard(post.content)}
                      className="bg-gray-100 text-gray-700 px-3 py-1 rounded hover:bg-gray-200 text-sm"
                    >
                      📋 Copy Text
                    </button>
                    {post.image_base64 && (
                      <button
                        onClick={() => handleGenerateImage(post.id)}
                        disabled={generatingImage === post.id}
                        className="bg-purple-100 text-purple-700 px-3 py-1 rounded hover:bg-purple-200 disabled:opacity-50 text-sm"
                      >
                        {generatingImage === post.id ? '⏳...' : '🔄 New Image'}
                      </button>
                    )}
                    {/* Post directly to GBP via API */}
                    {post.status !== 'posted' && (
                      <button 
                        onClick={() => handlePostToGBP(post)}
                        disabled={postingToGBP === post.id}
                        className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
                        data-testid={`post-to-google-${post.id}`}
                      >
                        {postingToGBP === post.id ? 'Posting...' : 'Post to Google'}
                      </button>
                    )}
                    {post.status !== 'posted' ? (
                      <button 
                        onClick={() => handleUpdateStatus('posts', post.id, 'posted')}
                        className="bg-green-100 text-green-700 px-3 py-1 rounded hover:bg-green-200 text-sm"
                      >
                        ✓ Mark Posted
                      </button>
                    ) : (
                      <button 
                        onClick={() => handleUpdateStatus('posts', post.id, 'draft')}
                        className="bg-gray-100 text-gray-600 px-3 py-1 rounded hover:bg-gray-200 text-sm"
                      >
                        ↩ Revert to Draft
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Review Responses Tab */}
      {activeTab === 'reviews' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Generate Response Form */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="font-semibold text-gray-800 mb-4">Generate Review Response</h2>
            <form onSubmit={handleGenerateReviewResponse} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-600 mb-1">Reviewer Name</label>
                <input
                  type="text"
                  value={reviewForm.reviewer_name}
                  onChange={e => setReviewForm({...reviewForm, reviewer_name: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  placeholder="John D."
                  required
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Rating</label>
                <select
                  value={reviewForm.rating}
                  onChange={e => setReviewForm({...reviewForm, rating: parseInt(e.target.value)})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value={5}>⭐⭐⭐⭐⭐ 5 Stars</option>
                  <option value={4}>⭐⭐⭐⭐ 4 Stars</option>
                  <option value={3}>⭐⭐⭐ 3 Stars</option>
                  <option value={2}>⭐⭐ 2 Stars</option>
                  <option value={1}>⭐ 1 Star</option>
                </select>
              </div>
              <div>
                <label className="block text-sm text-gray-600 mb-1">Review Text</label>
                <textarea
                  value={reviewForm.review_text}
                  onChange={e => setReviewForm({...reviewForm, review_text: e.target.value})}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 h-24"
                  placeholder="Paste the review text here..."
                  required
                />
              </div>
              <button
                type="submit"
                disabled={generating}
                className="w-full bg-teal-600 text-white py-2 rounded-lg hover:bg-teal-700 disabled:opacity-50"
              >
                {generating ? 'Generating...' : '✨ Generate Response'}
              </button>
            </form>
          </div>

          {/* Generated Responses */}
          <div className="lg:col-span-2">
            <h2 className="font-semibold text-gray-800 mb-4">Generated Responses ({responses.length})</h2>
            <div className="space-y-4 max-h-[600px] overflow-y-auto">
              {responses.length === 0 ? (
                <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
                  <p>No responses generated yet. Use the form to generate review responses!</p>
                </div>
              ) : (
                responses.map(resp => (
                  <div key={resp.id} className="bg-white rounded-lg shadow p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <span className="font-medium">{resp.reviewer_name}</span>
                        <span className="ml-2 text-yellow-500">{'⭐'.repeat(resp.review_rating)}</span>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        resp.status === 'posted' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {resp.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-2 italic">"{resp.review_text}"</p>
                    <div className="bg-teal-50 p-3 rounded-lg mb-3">
                      <p className="text-sm font-medium text-teal-800 mb-1">AI Response:</p>
                      <p className="text-sm text-gray-700">{resp.generated_response}</p>
                    </div>
                    <div className="flex gap-2">
                      <button 
                        onClick={() => copyToClipboard(resp.generated_response)}
                        className="bg-gray-100 text-gray-700 px-3 py-1 rounded hover:bg-gray-200 text-sm"
                      >
                        📋 Copy
                      </button>
                      <button 
                        onClick={() => handleUpdateStatus('review-responses', resp.id, 'posted')}
                        className="bg-green-100 text-green-700 px-3 py-1 rounded hover:bg-green-200 text-sm"
                      >
                        ✓ Mark Posted
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Q&A Tab */}
      {activeTab === 'qa' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="font-semibold text-gray-800">Q&A Suggestions</h2>
            <button 
              onClick={handleGenerateQA}
              disabled={generating}
              className="bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              + Generate Q&As
            </button>
          </div>
          
          {qaSuggestions.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              <p>No Q&As generated yet. Click "Generate Q&As" to create voice-search optimized questions!</p>
            </div>
          ) : (
            <div className="grid gap-4">
              {qaSuggestions.map(qa => (
                <div key={qa.id} className="bg-white rounded-lg shadow p-4">
                  <div className="flex justify-between items-start mb-2">
                    <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs">
                      {qa.category?.replace(/_/g, ' ')}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      qa.status === 'posted' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {qa.status}
                    </span>
                  </div>
                  <h3 className="font-semibold text-gray-800 mb-2">Q: {qa.question}</h3>
                  <p className="text-sm text-gray-600 mb-3 bg-gray-50 p-3 rounded-lg">A: {qa.answer}</p>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => {
                        copyToClipboard(`Question: ${qa.question}\n\nAnswer: ${qa.answer}`);
                      }}
                      className="bg-gray-100 text-gray-700 px-3 py-1 rounded hover:bg-gray-200 text-sm"
                    >
                      📋 Copy Both
                    </button>
                    <button 
                      onClick={() => handleUpdateStatus('qa-suggestions', qa.id, 'posted')}
                      className="bg-green-100 text-green-700 px-3 py-1 rounded hover:bg-green-200 text-sm"
                    >
                      ✓ Mark Added
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Competitor Tab */}
      {activeTab === 'competitor' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="font-semibold text-gray-800">Competitor Monitoring</h2>
            <button 
              onClick={handleCheckCompetitor}
              className="bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700"
            >
              🔍 Check Now
            </button>
          </div>
          
          <div className="bg-white rounded-lg shadow p-6 mb-4">
            <h3 className="font-semibold text-gray-800 mb-3">Tracking: Fort Dental (fortdentaltx.com)</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Their Reviews</p>
                <p className="text-xl font-bold text-gray-800">392</p>
              </div>
              <div>
                <p className="text-gray-600">Your Reviews</p>
                <p className="text-xl font-bold text-green-600">1,100+</p>
              </div>
            </div>
            <div className="mt-4 p-3 bg-green-50 rounded-lg">
              <p className="text-green-700 text-sm font-medium">🎉 You're winning! You have 698 more reviews than your competitor.</p>
            </div>
          </div>

          {competitorUpdates.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              <p>No competitor updates detected yet. Click "Check Now" to scan for changes.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {competitorUpdates.map(update => (
                <div key={update.id} className="bg-white rounded-lg shadow p-4 border-l-4 border-orange-500">
                  <div className="flex justify-between items-start mb-2">
                    <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-xs">
                      {update.update_type?.replace(/_/g, ' ')}
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(update.detected_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-sm text-gray-700 mb-2">{update.details}</p>
                  {update.action_suggested && (
                    <p className="text-sm text-teal-700 bg-teal-50 p-2 rounded">
                      💡 Suggested Action: {update.action_suggested}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Calendar Tab */}
      {activeTab === 'calendar' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h2 className="font-semibold text-gray-800">Content Calendar</h2>
            <button 
              onClick={handleGenerateCalendar}
              className="bg-teal-600 text-white px-4 py-2 rounded-lg hover:bg-teal-700"
            >
              📅 Generate 4-Week Calendar
            </button>
          </div>
          
          {calendar.length === 0 ? (
            <div className="bg-white rounded-lg shadow p-8 text-center text-gray-500">
              <p>No calendar items yet. Click "Generate 4-Week Calendar" to create a content schedule!</p>
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Task</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {calendar.map(item => (
                    <tr key={item.id} className={item.status === 'completed' ? 'bg-green-50' : ''}>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {new Date(item.scheduled_date).toLocaleDateString('en-US', { 
                          weekday: 'short', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{item.title}</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs ${
                          item.content_type === 'google_post' ? 'bg-blue-100 text-blue-700' :
                          item.content_type === 'qa' ? 'bg-purple-100 text-purple-700' :
                          item.content_type === 'review_response' ? 'bg-green-100 text-green-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {item.content_type?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${
                          item.status === 'completed' ? 'bg-green-100 text-green-800' :
                          item.status === 'skipped' ? 'bg-gray-100 text-gray-800' :
                          'bg-yellow-100 text-yellow-800'
                        }`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {item.status === 'scheduled' && (
                          <button
                            onClick={() => handleUpdateStatus('calendar', item.id, 'completed')}
                            className="text-green-600 hover:text-green-800 text-sm"
                          >
                            ✓ Complete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Atención en Español Tab — Spanish-language GBP signaling */}
      {activeTab === 'spanish' && (
        <div className="space-y-6" data-testid="gbp-spanish-tab">
          {/* Hero card: one-click brand badge */}
          <div className="bg-gradient-to-br from-rose-50 via-amber-50 to-emerald-50 border-2 border-amber-300 rounded-xl p-6 shadow-md">
            <div className="flex items-start justify-between gap-6 flex-wrap">
              <div className="flex-1 min-w-[280px]">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-2xl">🇲🇽</span>
                  <h2 className="text-xl font-bold text-gray-900">Atención en Español</h2>
                  <span className="text-xs font-semibold bg-amber-200 text-amber-900 px-2 py-0.5 rounded-full">
                    es-US signal
                  </span>
                </div>
                <p className="text-gray-700 mb-3 text-sm leading-relaxed">
                  Publicar el badge "Hablamos Español" a tu Google Business Profile.
                  El campo <code className="bg-white px-1 rounded text-xs">languageCode: es-US</code> es la señal
                  más fuerte que Google usa para decidir qué post mostrar a usuarios hispanohablantes en Maps.
                  Recomendado: 1× por trimestre como mínimo para mantener el bilingual signal vivo.
                </p>
                {esTemplates?.brand?.[0] && (
                  <div className="bg-white/70 border border-amber-200 rounded-lg p-3 mt-2">
                    <p className="text-xs font-semibold text-gray-600 mb-1">PREVIEW:</p>
                    <p className="text-sm text-gray-800 italic">"{esTemplates.brand[0].summary}"</p>
                  </div>
                )}
              </div>
              <button
                onClick={handlePostSpanishBadge}
                disabled={esBadgePosting || !gbpConnected}
                data-testid="post-spanish-badge-btn"
                className="bg-amber-600 hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-bold px-6 py-3 rounded-lg shadow-md transition-colors whitespace-nowrap"
              >
                {esBadgePosting ? 'Publicando...' : '🚀 Publicar Badge en GBP'}
              </button>
            </div>
            {!gbpConnected && (
              <p className="text-xs text-red-600 mt-3">⚠️ GBP no conectado. Conecta en Review Responses → Connect Google Business.</p>
            )}
          </div>

          {/* Spanish offer + service templates */}
          {!esTemplates ? (
            <div className="text-gray-500 text-sm">Cargando plantillas en español...</div>
          ) : (
            <>
              <div>
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <span>🎯</span> Ofertas en Español ({esTemplates.offers?.length || 0})
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {(esTemplates.offers || []).map((tpl, i) => (
                    <div key={`es-offer-${i}`} className="bg-white rounded-lg shadow border border-gray-200 p-4" data-testid={`es-offer-${i}`}>
                      <div className="flex justify-between items-start gap-2 mb-2">
                        <h4 className="font-semibold text-gray-900">{tpl.title}</h4>
                        <span className="text-xs bg-rose-100 text-rose-700 px-2 py-0.5 rounded-full">{tpl.cta_type}</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3 line-clamp-3">{tpl.summary}</p>
                      <div className="flex justify-between items-center text-xs text-gray-500 mb-3">
                        <span className="truncate">↗ {tpl.cta_url.replace('https://fortworthdental.com', '')}</span>
                      </div>
                      <button
                        onClick={() => handlePostEsTemplate(tpl, `offer-${i}`)}
                        disabled={esPosting === `offer-${i}` || !gbpConnected}
                        className="w-full bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-semibold py-2 rounded transition-colors"
                      >
                        {esPosting === `offer-${i}` ? 'Publicando...' : 'Publicar en GBP (es-US)'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
                  <span>🦷</span> Servicios en Español ({esTemplates.services?.length || 0})
                </h3>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {(esTemplates.services || []).map((tpl, i) => (
                    <div key={`es-svc-${i}`} className="bg-white rounded-lg shadow border border-gray-200 p-4" data-testid={`es-service-${i}`}>
                      <div className="flex justify-between items-start gap-2 mb-2">
                        <h4 className="font-semibold text-gray-900">{tpl.title}</h4>
                        <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{tpl.cta_type}</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3 line-clamp-3">{tpl.summary}</p>
                      <div className="flex justify-between items-center text-xs text-gray-500 mb-3">
                        <span className="truncate">↗ {tpl.cta_url.replace('https://fortworthdental.com', '')}</span>
                      </div>
                      <button
                        onClick={() => handlePostEsTemplate(tpl, `svc-${i}`)}
                        disabled={esPosting === `svc-${i}` || !gbpConnected}
                        className="w-full bg-teal-600 hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-semibold py-2 rounded transition-colors"
                      >
                        {esPosting === `svc-${i}` ? 'Publicando...' : 'Publicar en GBP (es-US)'}
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-900">
                <p className="font-semibold mb-1">💡 Cómo funciona el bilingual signal en GBP</p>
                <ul className="list-disc ml-5 space-y-1 text-blue-800">
                  <li>Google usa <code>languageCode</code> en cada post para decidir a quién mostrarlo en Maps.</li>
                  <li>Al publicar posts en <strong>es-US</strong> consistentemente, tu perfil se vuelve elegible para resultados en español ("dentista cerca de mí" en español).</li>
                  <li>Los CTAs apuntan a páginas en inglés <em>por ahora</em> — se actualizarán a <code>/es/...</code> cuando lance la conversión bilingüe (cambio de una sola línea: <code>ES_CTA_BASE</code> en <code>gbp_posts.py</code>).</li>
                  <li>Mantén ritmo: 1 post en español por semana mínimo, alterna con posts en inglés.</li>
                </ul>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default GBPAutomation;
